// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { UseQueryResult } from '@tanstack/react-query';
import { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { Variant } from '@mui/material/styles/createTypography';
import {
  DataGrid,
  GridColDef,
  GridValidRowModel,
  GridRowIdGetter,
  GridFilterModel,
  GridCallbackDetails,
  GridSortModel,
} from '@mui/x-data-grid';
import { TableWrapper } from './TableWrapper';
import { dataGridSx } from './dataGridSx';
import { withScrollableCell } from './withScrollableCell';

// Custom type that adds the 'hide' property to GridColDef.
type CustomGridColDef<R extends GridValidRowModel> = GridColDef<R> & {
  hide?: boolean;
};

type QueryTableProps<R extends GridValidRowModel> = {
  title: string;
  variant: Variant;
  columns: CustomGridColDef<R>[];
  rowQuery: UseQueryResult<R[], string>;
  rowKey: GridRowIdGetter<R>;
  toolbar?: React.FC<{ selected: R[] }>;
  onFilterModelChange?: (model: GridFilterModel, details: GridCallbackDetails<'filter'>) => void;
  defaultSortModel?: GridSortModel;
  enableTableWrapper?: boolean;
};

export const QueryTable = <R extends GridValidRowModel>({
  title,
  variant,
  columns,
  rowQuery,
  rowKey,
  toolbar,
  onFilterModelChange,
  defaultSortModel,
  enableTableWrapper,
}: QueryTableProps<R>): JSX.Element => {
  const [sortModel, setSortModel] = useState<GridSortModel>(defaultSortModel || []);
  // Filter out columns where hide is true
  const visibleColumns = columns.filter((column) => !column.hide);
  const table = (
    <DataGrid
      disableVirtualization
      aria-label={title}
      sx={(theme) => dataGridSx(theme)}
      columns={withScrollableCell(visibleColumns)}
      rows={rowQuery.isSuccess ? rowQuery.data : []}
      getRowId={rowKey}
      slots={{ toolbar }}
      onFilterModelChange={onFilterModelChange}
      loading={rowQuery.isLoading}
      sortModel={sortModel}
      onSortModelChange={setSortModel}
      rowSelection={false}
    />
  );
  if (enableTableWrapper) {
    return (
      <>
        {title && (
          <Typography sx={{ mt: 2, mb: 2 }} color="primary" variant={variant}>
            {title}
          </Typography>
        )}
        <TableWrapper>{table}</TableWrapper>
      </>
    );
  }
  return (
    <Box>
      <Typography sx={{ mt: 2, mb: 2 }} color="primary" variant={variant}>
        {title}
      </Typography>
      {table}
    </Box>
  );
};
// MuiDataGrid-virtualScrollerContent
